import { Component, OnInit, ComponentRef, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { Shell, PageComponent, PageService, Transfer, Retry, Page, sleep, OpenGraph, Head, Cache, AppConfig } from '@tytapp/common';
import { LiveStream, User, Production, Article, VOD } from '@tytapp/models';
import { UserService, LoginComponent } from '@tytapp/user';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { PlayerService } from '@tytapp/media-playback';
import { ApiCarouselSlide, FollowApi, ApiFollowedTopic, ApiTopic, ApiShow, ApiFollowedShow, ApiTopicItem, UsersApi, ApiPlaybackSession, ApiVOD, ApiPodcast, ApiProduction, ApiHomePage, ApiPoll } from '@tytapp/api';
import { PageApiService } from '@tytapp/common-ui';
import { GoogleCastService } from '@tytapp/media-playback';
import { AirPlayService } from 'media-playback/airplay.service';
import { ApiHomeRow } from 'api/model/HomeRow';
import { Meta } from '@angular/platform-browser';

@Page('home')
@Component({ 
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent extends PageComponent implements OnInit {

  constructor(
    pageService : PageService,
    private userApi : UsersApi,
    private userService : UserService,
    private pageApiService : PageApiService,
    private followApi : FollowApi,
    private opengraph : OpenGraph,
    private head : Head,
    private appConfig : AppConfig
  ) { 
    super(pageService);
  }

  get pageComponentId() {
    return 'home';
  }

  get isDevelopment() {
    return this.appConfig.environment == 'staging' 
      || this.appConfig.environment == 'development'
    ;
  }
  
  @Transfer() 
  ready : boolean = false;

  @Transfer()
  user : User;

  @Transfer()
  poll : ApiPoll;

  @Transfer()
  petition : ApiPoll;
  
  @Transfer()
  candidates: Production[];

  @Transfer()
  episodes: Production[];

  @Transfer()
  podcasts: Production[];

  @Transfer()
  clips: Production[];

  @Transfer()
  mainShowProds: Production[];

  @Transfer()
  articles: Article[];

  @Transfer()
  carouselSlides : ApiCarouselSlide[];

  get isServerSide() {
    return typeof window === 'undefined';
  }

  @ViewChild('airplay')
  airplay : ElementRef;

  @Transfer()
  audienceType : string = 'visitor';
  
  @Transfer()
  homeRows : ApiHomeRow[] = undefined;

  @Transfer()
  follows : ApiTopic[] = undefined;

  @Transfer()
  followedShows : ApiFollowedShow[] = undefined;

  isTopicFollowed(topic : ApiTopic) {
    if (!topic)
      return false;
    
    if (!this.follows)
      return false;
    return this.follows.some(x => x.slug === topic.slug);
  }

  sizeClass(homeRow : ApiHomeRow) {
    if (homeRow.type === 'must-see' || homeRow.topic_items.length == 4)
      return { type: 'double', tall: true };

    if (homeRow.topic_items.length == 2)
      return { type: 'double', tall: false };
    
    if (homeRow.topic_items.length == 0)
      return { type: 'skip', tall: false };

    if (homeRow.topic_items.length == 1)
      return { type: 'single', tall: false };
    
    if (homeRow.topic_items.length == 3)
      return { type: 'full', tall: false }

    return { type: 'full', tall: true };
  }

  limitArray(array : any[], length) {
    if (!array)
      return [];
    
    return array.slice(0, length);
  }

  mainVodOfProduction(production : Production) {
    if (production.full_length_vods && production.full_length_vods.length > 0)
      return production.full_length_vods[0];

    if (production.vod_clips && production.vod_clips.length > 0)
      return production.vod_clips[0];

    return null;
  }

  mainVodArrayOfPlaybackSession(playbackSession : ApiPlaybackSession) {
    let vod = this.mainVodOfPlaybackSession(playbackSession);

    if (vod)
      return [ vod ];

    return [];
  }

  mainVodOfPlaybackSession(playbackSession : ApiPlaybackSession) {

    if (!playbackSession)
      return null;
    
    if (!playbackSession.production) {
      console.error(`Encountered a playback session without a production!`);
      console.error(playbackSession.production);
      return null;
    }

    let production = playbackSession.production;
    let vod : ApiVOD | ApiPodcast;

    if (playbackSession.playlist_id === 'clips') {
      vod = playbackSession.production.vod_clips.find(x => x.id === playbackSession.playlist_item_id);
    } else if (playbackSession.playlist_id === 'episodes') {
      vod = playbackSession.production.full_length_vods.find(x => x.id === playbackSession.playlist_item_id);
    } else if (playbackSession.playlist_id === 'podcasts') {
      vod = playbackSession.production.full_length_podcasts.find(x => x.id === playbackSession.playlist_item_id);
    }

    return vod;
  }

  unlockedStatusOfVod(vod : VOD) {
    if (!vod)
      return vod;
    
    if (!vod.unlock_premium)
      return false;

    if (!vod.unlock_premium_expiration)
      return true;
    
    
    let date = new Date(vod.unlock_premium_expiration);

    return date.getTime() > Date.now();
  }

  linkForVod(production, vod) {
    let isEpisode = production.full_length_vods.some(x => x.id === vod.id);
    let mode = isEpisode ? 'episodes' : 'clips';

    return `/watch/${production.show.slug || production.show.id}/${production.id}/${mode}/${vod.id}`;
  }

  isShowFollowed(show : ApiShow) {
    if (!this.followedShows)
      return false;

    return this.followedShows.some(x => x.id === show.id);
  }

  async followTopic(topic : ApiTopic) {
    if (!this.user) {
      this.router.navigateByUrl('/signup');
      return;
    }

    let followedTopic : ApiFollowedTopic = <any>topic;
    followedTopic.is_followed = true;
    await this.followApi.followTopic(topic).toPromise();

    this.follows.push(topic);
  }

  spotIdForTopicItem(topicItem : ApiTopicItem) {
    if (topicItem.type === 'vod')
      return `vod_${topicItem.vod_id}`;
    if (topicItem.type === 'article')
      return `article_${topicItem.article_id}`;

    return null;
  }

  playlistForTopicItem(topicItem : ApiTopicItem) {
    if (topicItem.type === 'vod') {
      return topicItem.vod_type === 'episode' ? 'episodes' : 'clips';
    } else if (topicItem.type === 'article') {
      return 'articles';
    }

    return 'clips';
  }
  
  playlistForMainVodOfProduction(production : ApiProduction) {
    if (production.full_length_vods && production.full_length_vods.length > 0)
      return 'episodes';

    return 'clips';
  }
  
  badgeTypeForTopicItem(topicItem : ApiTopicItem) {
    if (topicItem.type === 'vod') {
      return topicItem.vod_type;
    } else if (topicItem.type === 'article') {
      return 'article';
    }

    return 'clip';
  }

  linkForTopicItem(topicItem : ApiTopicItem) {
    if (topicItem.type === 'vod') {
      return `/watch/${topicItem.brand_id}/${topicItem.production_id}/${topicItem.vod_type}s/${topicItem.vod_id}`;    
    } else if (topicItem.type === 'article') {
      return `/stories/${topicItem.brand_id}/${topicItem.article_id}`;
    }

    // uh oh
    return `javascript:;`;
  }

  async unfollowTopic(topic : ApiTopic) {
    if (!this.user) {
      this.router.navigateByUrl('/signup');
      return;
    }

    let unfollowedTopic : ApiFollowedTopic = <any>topic;
    unfollowedTopic.is_followed = false;
    await this.followApi.followTopic(topic).toPromise();
    this.follows = this.follows.filter(x => x.slug !== topic.slug);
  }

  @Transfer()
  playbackSessions : ApiPlaybackSession[] = null;

  @HostBinding('class.animate-in')
  animateIn = false;

  async buildRows(homePage : ApiHomePage) {
    if (homePage.rows) {
      let finalRows = []
      let sprinkledRows : ApiHomeRow[] = <any>[
        { type: 'newsletter' },
        { type: 'tune-in' },
        //{ type: 'reporting' }
      ];

      let rowIndex = 0;
      for (let row of homePage.rows) {
        if (rowIndex > 0 && rowIndex % 3 === 0) {
          let sprinkledRow = sprinkledRows.shift();
          if (sprinkledRow)
            finalRows.push(sprinkledRow);
        }

        if (rowIndex > 3) {
          row.topic_items = row.topic_items.slice(0, Math.floor(1 + Math.random() * 2));
        }

        finalRows.push(row);

        ++rowIndex;
      }

      for (let sprinkledRow of sprinkledRows) {
        finalRows.push(sprinkledRow);
      }

      finalRows.push({ type: 'election' });

      this.homeRows = finalRows;
    }

    await sleep(500);
    this.animateIn = true;
  }

  async fetchPlaybackSessions() {
    await this.userService.ready;
    
    this.playbackSessions = null;
    if (this.user) {
      this.playbackSessions = await this.userApi.getOpenSessions().toPromise();
    } else {
      this.playbackSessions = [];
    }
  }

  failedToLoad = false;

  anonymousHomeCache = Cache.shared<ApiHomePage>('home-anonymous', 1000*60*10, 1);
  homeCache = Cache.shared<ApiHomePage>('home', 1000*60*5, 100);

  async init() {
    this.head.setCanonicalLink('/');

    this.opengraph.setLdJson({ 
      "@context" : "http://schema.org",
      "@type" : "Organization",
      "name" : "The Young Turks",
      "url" : "https://tyt.com",
      "sameAs" : [ 
        "https://www.facebook.com/TheYoungTurks",
        "https://twitter.com/TheYoungTurks",
        "http://www.youtube.com/theyoungturks",
        "https://www.linkedin.com/company/the-young-turks",
        "http://tytnetwork.tumblr.com/",
        "http://www.pinterest.com/tytnetwork",
        "http://instagram.com/tytnetwork",
        "https://plus.google.com/+TheYoungTurks"] 
    });

    await this.userService.ready;

    if (!this.hasServerState) {
      this.fetchPlaybackSessions();

      let homePage : ApiHomePage;
      
      try {
        if (!this.userService.user) {
          homePage = await this.anonymousHomeCache.fetch('default', async () => await this.pageApiService.home());
        } else {
          homePage = await this.homeCache.fetch(`user_${this.userService.user.id}`, async () => await this.pageApiService.home());
        }
      } catch (e) {
        console.error(`Failed to load home page:`);
        console.error(e);
        homePage = null;
      }

      if (!homePage) {
        this.failedToLoad = true;
        return;
      }

      this.poll = homePage.poll;
      this.petition = homePage.petition;
      this.carouselSlides = homePage.carousel_slides;
      this.candidates = <Production[]>homePage.candidates;
      this.candidates = this.candidates.filter((candidate) => !homePage.main_show || candidate.id != homePage.main_show.id);
      this.pageTitle = 'Home';
      this.ready = true;

      this.buildRows(homePage);

      //this.articles = <Article[]>homePage.articles;
      //this.episodes = <Production[]>homePage.episodes;
      //this.episodes = this.episodes.filter((episode) => !homePage.main_show || episode.id != homePage.main_show.id);
      //this.clips = <Production[]>homePage.clips;
      //this.clips = this.clips.filter((clip) => !homePage.main_show || clip.id != homePage.main_show.id);
      //this.podcasts = <Production[]>homePage.podcasts;
      //this.mainShowProds = [homePage.main_show];

    } else {
      await sleep(1);
      this.animateIn = true;
    }

    this.subscribe(this.userService.onboardingChanged, onboarding => this.onboarding = onboarding);
    this.subscribe(this.userService.userChanged, async user => {
      this.fetchPlaybackSessions()
      this.user = user;
      if (user) {
        this.follows = await this.followApi.getFollows().toPromise();
        this.followedShows = await this.followApi.getFollowedShows().toPromise();

        if (this.follows.length == 0 && this.followedShows.length == 0) {
          this.userService.onboarding = true;
        }
      }
    });

    this.audienceType = this.userService.audienceType;
    if (this.audienceType === 'registered') {
      // HACK
      // Treat registered users as "visitor" role for now for the purposes of the
      // home page carousel. This must be removed in order to have unique carousel slides
      // for registered users vs unregistered visitors
      // HACK
      this.audienceType = 'visitor';
    }
  }

  onboarding = false;

  get mainShowRoute() {
    if (!this.mainShowProds || !this.mainShowProds[0]) {
      return '/';
    }
    return `/shows/${this.mainShowProds[0].show.id}`;
  }
  
}
